import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/render/project/src/node_modules/.pnpm/gatsby-theme-docz@2.3.1_@babel+core@7.11.1_@types+node@24.7.0_docz@2.4.0_@babel+core@7._04842b0c5656885ffb9b2f355e30df2b/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "delaywrapper"
    }}>{`DelayWrapper`}</h1>
    <p>{`A React component that provides delay logic for loading states to prevent UI flashing for quick operations and ensure minimum display duration.`}</p>
    <h2 {...{
      "id": "features"
    }}>{`Features`}</h2>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Delay before showing`}</strong>{`: Prevents loading UI from appearing for quick operations (default: 100ms)`}</li>
      <li parentName="ul"><strong parentName="li">{`Minimum display duration`}</strong>{`: Ensures loading UI stays visible for a minimum time once shown (default: 200ms)`}</li>
      <li parentName="ul"><strong parentName="li">{`Customizable timing`}</strong>{`: Configurable delay and minimum duration values`}</li>
      <li parentName="ul"><strong parentName="li">{`Fallback support`}</strong>{`: Optional fallback UI to show while delayed`}</li>
      <li parentName="ul"><strong parentName="li">{`Reusable`}</strong>{`: Can wrap any loading UI component`}</li>
    </ul>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <h3 {...{
      "id": "basic-usage"
    }}>{`Basic Usage`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import DelayWrapper from '../flowbot/components/delay-wrapper'

function MyComponent ({ loading }) {
  return (
    <DelayWrapper loading={loading}>
      <Spinner size={16} />
    </DelayWrapper>
  )
}
`}</code></pre>
    <h3 {...{
      "id": "with-custom-timing"
    }}>{`With Custom Timing`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<DelayWrapper loading={loading} delay={150} minDuration={300}>
  <div className='loading-indicator'>Loading...</div>
</DelayWrapper>
`}</code></pre>
    <h3 {...{
      "id": "with-fallback-ui"
    }}>{`With Fallback UI`}</h3>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<DelayWrapper
  loading={loading}
  fallback={<div className='placeholder'>Ready to load...</div>}
>
  <Spinner size={16} />
</DelayWrapper>
`}</code></pre>
    <h3 {...{
      "id": "in-lookup-controls"
    }}>{`In Lookup Controls`}</h3>
    <p>{`The `}<inlineCode parentName="p">{`PositionedSpinner`}</inlineCode>{` component in `}<inlineCode parentName="p">{`lookup-controls.jsx`}</inlineCode>{` uses `}<inlineCode parentName="p">{`DelayWrapper`}</inlineCode>{` internally:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`<PositionedSpinner loading={loading} gridded={props.gridded} size={16} />
`}</code></pre>
    <h2 {...{
      "id": "props"
    }}>{`Props`}</h2>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Default`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`loading`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`-`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The loading state`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`delay`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`100`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Delay before showing the loading UI (in milliseconds)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minDuration`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`200`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Minimum duration to show the loading UI (in milliseconds)`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`children`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`-`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The loading UI component to render`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fallback`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`React.ReactNode`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional fallback UI to show while delayed`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "behavior"
    }}>{`Behavior`}</h2>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Loading starts`}</strong>{`: Component waits for `}<inlineCode parentName="li">{`delay`}</inlineCode>{` milliseconds before showing loading UI`}</li>
      <li parentName="ol"><strong parentName="li">{`Loading stops before delay`}</strong>{`: Loading UI never appears, component returns to initial state`}</li>
      <li parentName="ol"><strong parentName="li">{`Loading stops after delay but before minDuration`}</strong>{`: Loading UI continues to show for the remaining time to reach `}<inlineCode parentName="li">{`minDuration`}</inlineCode></li>
      <li parentName="ol"><strong parentName="li">{`Loading stops after minDuration`}</strong>{`: Loading UI disappears immediately`}</li>
      <li parentName="ol"><strong parentName="li">{`New loading starts`}</strong>{`: Resets the delay timer and starts the process again`}</li>
    </ol>
    <h2 {...{
      "id": "examples"
    }}>{`Examples`}</h2>
    <h3 {...{
      "id": "quick-query-sub-100ms"
    }}>{`Quick Query (Sub-100ms)`}</h3>
    <pre><code parentName="pre" {...{}}>{`Loading starts → Wait 100ms → Loading stops → UI never shows
`}</code></pre>
    <h3 {...{
      "id": "medium-query-200ms-total-stops-at-150ms-after-showing"
    }}>{`Medium Query (200ms total, stops at 150ms after showing)`}</h3>
    <pre><code parentName="pre" {...{}}>{`Loading starts → Wait 100ms → Show UI → Loading stops at 150ms → Wait 50ms → Hide UI
`}</code></pre>
    <h3 {...{
      "id": "long-query-500ms-total"
    }}>{`Long Query (500ms total)`}</h3>
    <pre><code parentName="pre" {...{}}>{`Loading starts → Wait 100ms → Show UI → Loading stops at 500ms → Hide UI immediately
`}</code></pre>
    <p>{`The key insight is that `}<inlineCode parentName="p">{`minDuration`}</inlineCode>{` only applies when loading stops before the minimum time has elapsed. If loading continues beyond the minimum duration, the loading UI disappears immediately when loading stops.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      